import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type PortalNotification,
} from "@/services/notificationService";

const TYPE_META: Record<string, { icon: string; className: string }> = {
  lead:        { icon: "ri-mail-line",            className: "bg-primary-100 text-primary-700" },
  review:      { icon: "ri-star-line",            className: "bg-accent-100 text-accent-700" },
  review_flag: { icon: "ri-flag-2-line",          className: "bg-rose-100 text-rose-700" },
  listing:     { icon: "ri-store-3-line",         className: "bg-secondary-100 text-secondary-700" },
  order:       { icon: "ri-shopping-bag-3-line",  className: "bg-emerald-100 text-emerald-700" },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PortalNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setError("");
      const result = await getNotifications(accessToken);
      setItems(result.data);
      setUnread(result.unreadCount);
    } catch {
      setError("Could not load notifications.");
      setItems([]);
      setUnread(0);
    }
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  // Close on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function handleOpenItem(item: PortalNotification) {
    if (!item.isRead) {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isRead: true } : i)));
      setUnread((u) => Math.max(0, u - 1));
      markNotificationRead(accessToken, item.id).catch(() => { /* best-effort */ });
    }
    setOpen(false);
    if (item.link) navigate(item.link);
  }

  async function handleMarkAll() {
    setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
    setUnread(0);
    await markAllNotificationsRead(accessToken).catch(() => { /* best-effort */ });
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
      >
        <i className="ri-notification-3-line text-lg" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-lg">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
            <p className="font-heading text-sm font-bold text-foreground-950">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="cursor-pointer whitespace-nowrap text-[11px] font-semibold text-primary-600 transition-colors hover:text-primary-700"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
                <i className={`${error ? "ri-error-warning-line text-rose-400" : "ri-notification-off-line text-foreground-300"} text-2xl`} />
                <p className={`mt-2 text-xs ${error ? "text-rose-600" : "text-foreground-500"}`}>
                  {error || "You're all caught up."}
                </p>
              </div>
            ) : (
              items.map((item) => {
                const meta = TYPE_META[item.type] ?? { icon: "ri-notification-3-line", className: "bg-background-100 text-foreground-600" };
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleOpenItem(item)}
                    className={`flex w-full cursor-pointer items-start gap-3 border-b border-background-100 px-4 py-3 text-left transition-colors hover:bg-background-100 ${
                      !item.isRead ? "bg-primary-50/40" : ""
                    }`}
                  >
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.className}`}>
                      <i className={`${meta.icon} text-sm`} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className={`truncate text-xs font-semibold ${!item.isRead ? "text-foreground-950" : "text-foreground-700"}`}>
                          {item.title}
                        </span>
                        {!item.isRead && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />}
                      </span>
                      <span className="mt-0.5 line-clamp-2 block text-[11px] leading-snug text-foreground-500">
                        {item.body}
                      </span>
                      <span className="mt-1 block text-[10px] text-foreground-400">{timeAgo(item.createdAt)}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}